// @ts-nocheck
(function attachNoDrillVisualization(root, factory) {
  const api = factory();
  if (typeof module === 'object' && module.exports) module.exports = api;
  else root.NoDrillVisualization = api;
})(typeof globalThis !== 'undefined' ? globalThis : this, function createNoDrillVisualization() {
  'use strict';

  const EXACT_SELECTION_FIELDS = [
    'fabric_family',
    'fabric_type',
    'installation_method',
    'mount_location',
    'fabric_color',
    'headrail_style',
    'hardware_finish',
    'control_method',
    'chain_material',
    'bottom_rail',
    'bottom_rail_finish',
    'accessory_visual_key',
  ];

  function isPlainSelectionRecord(selections) {
    return Boolean(selections)
      && typeof selections === 'object'
      && !Array.isArray(selections)
      && Object.getPrototypeOf(selections) === Object.prototype;
  }

  function hasOwnStringSelections(selections, fields) {
    return isPlainSelectionRecord(selections) && fields.every((key) => (
      Object.prototype.hasOwnProperty.call(selections, key)
      && typeof selections[key] === 'string'
    ));
  }

  function canonicalVisualChainMaterial(value, controlId) {
    if (controlId === 'pull_chain') {
      if (value === 'plastic') return 'plastic';
      if (value === 'stainless_steel') return 'metal';
      return null;
    }
    return value === '' ? 'none' : null;
  }

  function canonicalVisualBottomRailFinish(value) {
    if (value === '') return 'none';
    if (value === 'white' || value === 'beige' || value === 'black') return value;
    if (value === 'gray') return 'grey';
    return null;
  }

  function publicChainMaterial(value) {
    if (value === 'none') return '';
    if (value === 'metal') return 'stainless_steel';
    return value;
  }

  function publicBottomRailFinish(value) {
    if (value === 'none') return '';
    if (value === 'grey') return 'gray';
    return value;
  }

  function exactSelectionKey(selections, finishId, includeChainMaterial) {
    const chainMaterialId = includeChainMaterial
      ? canonicalVisualChainMaterial(selections.chain_material, selections.control_method)
      : 'none';
    const bottomRailFinishId = canonicalVisualBottomRailFinish(selections.bottom_rail_finish);
    if (chainMaterialId === null || bottomRailFinishId === null) return null;
    const values = [
      selections.installation_method,
      selections.mount_location,
      selections.fabric_color,
      selections.headrail_style,
      finishId,
      selections.control_method,
    ];
    if (includeChainMaterial) values.push(chainMaterialId);
    values.push(
      selections.bottom_rail,
      bottomRailFinishId,
      selections.accessory_visual_key || 'none',
    );
    return values.join('::');
  }

  function exactSceneSelectionKey(selections) {
    if (!hasOwnStringSelections(selections, EXACT_SELECTION_FIELDS)) return null;
    const chainMaterialId = canonicalVisualChainMaterial(
      selections.chain_material,
      selections.control_method,
    );
    const bottomRailFinishId = canonicalVisualBottomRailFinish(selections.bottom_rail_finish);
    if (chainMaterialId === null || bottomRailFinishId === null) return null;
    const values = [
      selections.fabric_family,
      selections.fabric_type,
      selections.installation_method,
      selections.mount_location,
      selections.fabric_color,
      selections.headrail_style,
      selections.hardware_finish,
      selections.control_method,
      chainMaterialId,
      selections.bottom_rail,
      bottomRailFinishId,
      selections.accessory_visual_key || 'none',
    ];
    if (values.some((value) => typeof value !== 'string' || value.length === 0)) return null;
    return values.join('::');
  }

  function resolveExactSceneReviewPreset(requestedFinish, locks, assets) {
    if (!requestedFinish || !locks?.fabric_family || !locks?.fabric_type) return null;
    const preset = assets?.reviewPresets?.[requestedFinish];
    if (preset?.options?.fabric_family === locks.fabric_family && preset?.options?.fabric_type === locks.fabric_type) {
      return {
        exactKey: `review-preset:${requestedFinish}`,
        options: { ...preset.options },
        control: preset.control,
        accessory: preset.accessory || 'none',
      };
    }
    const matches = Object.keys(assets?.exactScenes || {}).filter((exactKey) => {
      const values = exactKey.split('::');
      return values.length === 12
        && values[0] === locks.fabric_family
        && values[1] === locks.fabric_type
        && values[6] === requestedFinish;
    });
    if (matches.length !== 1) return null;
    const exactKey = matches[0];
    const values = exactKey.split('::');
    return {
      exactKey,
      options: {
        fabric_family: values[0],
        fabric_type: values[1],
        installation_method: values[2],
        mount_location: values[3],
        fabric_color: values[4],
        headrail_style: values[5],
        hardware_finish: values[6],
        chain_material: publicChainMaterial(values[8]),
        bottom_rail: values[9],
        bottom_rail_finish: publicBottomRailFinish(values[10]),
      },
      control: values[7],
      accessory: values[11],
    };
  }

  function validLayer(slot, layer) {
    if (!layer?.src || !layer?.mask) return null;
    const edges = ['top', 'right', 'bottom', 'left'];
    if (!edges.every((edge) => Number.isFinite(Number(layer.mask[edge])))) return null;
    return { slot, src: layer.src, mask: { ...layer.mask } };
  }

  function layeredSelectionMatches(selections, family, pendingSelection = '') {
    const pendingFields = pendingSelection === 'control_method'
      ? ['control_method', 'chain_material']
      : [pendingSelection];
    if (family.requireExplicitHardwareFinish && !selections.hardware_finish) return false;
    return Object.entries(family.requiredSelections || {}).every(([selectionId, allowedValues]) => (
      pendingFields.includes(selectionId) || (Array.isArray(allowedValues)
      && allowedValues.includes(selections[selectionId])
      )
    ));
  }

  function resolveLayeredFamily(selections, assets, pendingSelection = '') {
    const family = assets?.families?.[selections.fabric_family];
    if (!family) return null;
    const profiles = assets?.constructionProfiles?.[selections.fabric_family]
      || family.constructionProfiles;
    if (profiles && typeof profiles === 'object' && !Array.isArray(profiles)) {
      const matches = Object.values(profiles).filter((profile) => (
        profile && typeof profile === 'object'
        && layeredSelectionMatches(selections, profile, pendingSelection)
      ));
      if (matches.length > 1) return null;
      if (matches.length === 1) {
        return {
          ...family,
          ...matches[0],
          layers: matches[0].layers,
          requiredSelections: matches[0].requiredSelections,
        };
      }
    }
    return layeredSelectionMatches(selections, family, pendingSelection) ? family : null;
  }

  function resolveExactSceneVisual(selections, assets) {
    const exactKey = exactSceneSelectionKey(selections || {});
    if (!exactKey) return null;
    const controlAlias = assets?.controlVisualAliases?.[selections.control_method];
    const aliasedKey = controlAlias
      ? exactSceneSelectionKey({ ...selections, control_method: controlAlias, chain_material: '' })
      : '';
    const directSceneSrc = assets?.exactScenes?.[exactKey];
    const aliasedSceneSrc = aliasedKey ? assets?.exactScenes?.[aliasedKey] : '';
    const sceneSrc = directSceneSrc || aliasedSceneSrc;
    if (!sceneSrc) return null;
    const usesControlAlias = !directSceneSrc && Boolean(aliasedSceneSrc);
    let control = null;
    if (usesControlAlias) {
      const exactControlDefinition = assets?.exactControlLayersByMount?.[selections.mount_location]?.[selections.control_method]
        || assets?.exactControlLayers?.[selections.control_method];
      if (!exactControlDefinition) return null;
      const chainMaterialId = canonicalVisualChainMaterial(
        selections.chain_material,
        selections.control_method,
      );
      if (chainMaterialId === null) return null;
      const exactControlLayer = selections.control_method === 'pull_chain'
        ? exactControlDefinition[chainMaterialId]
        : exactControlDefinition;
      control = validLayer('control', exactControlLayer);
      if (!control) return null;
    }
    return {
      baseSceneSrc: sceneSrc,
      sceneSrc,
      layers: control ? [control] : [],
      constructionLabel: assets.exactSceneLabels?.[selections.fabric_family] || 'Shade',
      bottomRailStatus: selections.bottom_rail === 'fabric-covered'
        ? 'fabric-covered'
        : 'regular-finish-matched',
      exactKey: directSceneSrc ? exactKey : (aliasedKey || exactKey),
      visualMode: 'exact-scene',
      baseSceneRole: 'product-complete',
      presentationMode: 'portrait-contain',
    };
  }

  function resolveProgressiveVisual(selections, assets) {
    if (!hasOwnStringSelections(selections, EXACT_SELECTION_FIELDS)) return null;
    if ('installation_surface' in selections) return null;
    const pendingSelection = selections.control_method === '' && selections.chain_material === ''
      ? 'control_method'
      : (selections.control_method === 'pull_chain' && selections.chain_material === '' ? 'chain_material' : '');
    if (!pendingSelection) return null;
    if (selections.accessory_visual_key !== 'none') return null;

    const layered = resolveLayeredVisual(selections, assets, {
      allowPendingControl: true,
      pendingSelection,
    });
    if (layered?.visualMode === 'layered-scene') {
      return {
        ...layered,
        visualMode: 'provisional-layered-scene',
        pendingSelection,
      };
    }

    if (selections.installation_method === 'no-drill' && selections.mount_location === 'inside') {
      if (pendingSelection === 'chain_material') return null;
      if (selections.headrail_style !== 'no-drill-open-roll') return null;
      const exact = resolveExactSceneVisual({
        ...selections,
        control_method: 'cordless',
        chain_material: '',
      }, assets);
      if (!exact || exact.visualMode !== 'exact-scene' || exact.layers.length !== 0) return null;
      return {
        ...exact,
        visualMode: 'provisional-exact-scene',
        pendingSelection: 'control_method',
      };
    }

    if (selections.installation_method !== 'standard-bracket' || selections.mount_location !== 'outside') return null;
    if (pendingSelection === 'chain_material') return null;
    if (!['standard-open-roll', 'standard-bracket-cassette'].includes(selections.headrail_style)) return null;
    const resolved = resolveExactSceneVisual({
      ...selections,
      control_method: 'cordless',
      chain_material: '',
    }, assets);
    if (!resolved || resolved.visualMode !== 'exact-scene' || resolved.layers.length !== 0) return null;
    return {
      ...resolved,
      visualMode: 'provisional-exact-scene',
      pendingSelection: 'control_method',
    };
  }

  function resolveLayeredVisual(selections, assets, options = {}) {
    if (!hasOwnStringSelections(selections, EXACT_SELECTION_FIELDS)) return null;
    const family = resolveLayeredFamily(selections, assets, options.pendingSelection);
    if (!family?.baseSceneSrc) return null;
    const finishId = selections.hardware_finish || assets.defaultHardwareFinish;
    const staticDefinitions = family.layers?.static || [];
    if (!Array.isArray(staticDefinitions)) return null;
    const staticLayers = staticDefinitions.map((definition, index) => validLayer(`static-${index}`, definition));
    if (staticLayers.some((layer) => !layer)) return null;
    const fabric = validLayer('fabric', family.layers?.fabric?.[selections.fabric_color]);
    const headrail = validLayer(
      'headrail',
      family.layers?.headrail?.[selections.headrail_style]?.[finishId],
    );
    if (!fabric || !headrail) return null;

    const controlId = selections.control_method;
    const chainPending = options.pendingSelection === 'chain_material';
    const chainMaterialId = chainPending ? null : canonicalVisualChainMaterial(
      selections.chain_material,
      controlId,
    );
    if (!chainPending && chainMaterialId === null) return null;
    const layers = [...staticLayers, fabric, headrail];
    if (controlId && !chainPending) {
      const controlDefinition = family.layers?.control?.[controlId];
      if (!controlDefinition) return null;
      if (controlDefinition.visibleLayerRequired !== false) {
        const controlLayer = controlId === 'pull_chain'
          ? controlDefinition[chainMaterialId]
          : controlDefinition;
        const control = validLayer('control', controlLayer);
        if (!control) return null;
        layers.push(control);
      }
    } else if (!options.allowPendingControl) {
      return null;
    }

    const bottomRailDefinitions = family.layers?.bottomRail;
    if (family.requireBottomRailLayer || bottomRailDefinitions) {
      const bottomRailFinishId = canonicalVisualBottomRailFinish(selections.bottom_rail_finish);
      if (bottomRailFinishId === null) return null;
      if (selections.bottom_rail === 'fabric-covered' && selections.bottom_rail_finish !== '') return null;
      const expectedRegularFinish = family.regularBottomRailByHardware?.[finishId];
      if (selections.bottom_rail === 'regular' && expectedRegularFinish && bottomRailFinishId !== expectedRegularFinish) return null;
      const bottomRailDefinition = bottomRailDefinitions?.[selections.bottom_rail];
      const bottomRailSource = bottomRailDefinition?.byFabric
        ? bottomRailDefinition.byFabric?.[selections.fabric_color]
        : bottomRailDefinition?.[bottomRailFinishId];
      const bottomRail = validLayer(
        'bottom-rail',
        bottomRailSource,
      );
      if (!bottomRail) return null;
      layers.push(bottomRail);
    }

    return {
      baseSceneSrc: family.baseSceneSrc,
      sceneSrc: family.baseSceneSrc,
      layers,
      constructionLabel: family.label,
      bottomRailStatus: family.bottomRailStatus || `${selections.bottom_rail}-finish-independent`,
      ...(family.presentationMode ? { presentationMode: family.presentationMode } : {}),
      exactKey: exactSelectionKey(selections, finishId, true),
      visualMode: 'layered-scene',
      baseSceneRole: 'construction-free',
    };
  }

  function resolveLegacyVisual(selections, assets) {
    if (!assets || selections?.fabric_family !== assets.familyId) return null;
    const construction = assets.constructions?.[selections.fabric_type];
    const sceneSrc = construction?.scenes?.[selections.fabric_color];
    if (!sceneSrc) return null;
    const finishId = selections.hardware_finish || assets.defaultHardwareFinish;
    const exactKey = exactSelectionKey(selections, finishId, false);
    const exact = construction.exactCombinations?.[exactKey];
    if (!exact?.hardwareSrc) return null;
    return {
      sceneSrc: exact.sceneSrc || sceneSrc,
      hardwareSrc: exact.hardwareSrc,
      constructionLabel: construction.label,
      exactKey,
    };
  }

  function resolveReferenceVisual(selections, assets) {
    if (!isPlainSelectionRecord(selections)) return null;
    const family = assets?.families?.[selections.fabric_family];
    const familyReferences = family?.referenceScenes;
    const usesFamilyReferences = familyReferences
      && typeof familyReferences === 'object'
      && !Array.isArray(familyReferences);
    if (!usesFamilyReferences && selections.fabric_family !== assets?.familyId) return null;
    const references = usesFamilyReferences ? familyReferences : assets.referenceScenes;
    if (!references || typeof references !== 'object' || Array.isArray(references)) return null;
    const requestedHeadrail = typeof selections.headrail_style === 'string'
      ? selections.headrail_style
      : '';
    const defaultKey = usesFamilyReferences
      ? (typeof family.defaultReferenceSceneKey === 'string' ? family.defaultReferenceSceneKey : '')
      : (typeof assets.defaultReferenceSceneKey === 'string' ? assets.defaultReferenceSceneKey : '');
    const sceneSrc = references[requestedHeadrail] || references[defaultKey];
    if (typeof sceneSrc !== 'string' || !sceneSrc) return null;
    return {
      baseSceneSrc: sceneSrc,
      sceneSrc,
      layers: [],
      visualMode: 'reference-scene',
      presentationMode: 'portrait-contain',
    };
  }

  function resolveVisual(selections, assets) {
    if (!isPlainSelectionRecord(selections)) return null;
    if ('installation_surface' in selections) return null;
    const declaredFamily = assets?.families?.[selections.fabric_family];
    if (declaredFamily?.visualStrategy === 'layered') {
      const ownerApprovedExactScene = resolveExactSceneVisual(selections, assets);
      if (ownerApprovedExactScene) return ownerApprovedExactScene;
      return resolveLayeredVisual(selections, assets);
    }
    const exactScene = resolveExactSceneVisual(selections, assets);
    if (exactScene) return exactScene;
    if (assets?.schemaVersion === 2 && assets?.families) {
      return resolveLayeredVisual(selections, assets);
    }
    return resolveLegacyVisual(selections, assets);
  }

  return {
    resolveVisual,
    resolveProgressiveVisual,
    resolveReferenceVisual,
    resolveExactSceneReviewPreset,
  };
});
